import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ViewChild,
  inject,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PartnerDealResponse } from '@interfaces/partners/responses/partner-deal.response';
import { ConfirmationService } from '@services/helpers/confirmation.service';
import { FormErrorHandlerService } from '@services/helpers/form-error-handler.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { PartnersHttpService } from '@services/http/partners/partners-http.service';
import { MenuItem } from 'primeng/api';
import { ContextMenu, ContextMenuModule } from 'primeng/contextmenu';
import { Menu, MenuModule } from 'primeng/menu';
import { filter, switchMap } from 'rxjs';

/**
 * Menu de ações do card do deal em Meus deals (card 3324).
 *
 * Uma instância só para o board inteiro, e não uma por card: o card diz qual deal e de onde veio o
 * clique (reticências ou botão direito), e o menu monta as ações daquele deal. As duas entradas
 * usam **a mesma lista** de ações, como no Pipeline de Crédito, só mudando onde o menu abre: nas
 * reticências, colado ao botão; no clique direito, no ponto do clique.
 *
 * Hoje a única ação é "Apagar deal". Quem pode e em que etapa vem resolvido do servidor
 * (`canDelete` e `deleteBlockedReason`); fora da regra, a opção aparece desabilitada com o motivo
 * logo abaixo, e o `DELETE` revalida de todo modo.
 */
@Component({
  selector: 'app-deal-actions-menu',
  templateUrl: './deal-actions-menu.component.html',
  styleUrl: './deal-actions-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MenuModule, ContextMenuModule],
})
export class DealActionsMenuComponent {
  private readonly partnersService = inject(PartnersHttpService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly formErrorHandler = inject(FormErrorHandlerService);
  private readonly toast = inject(ToastHelperService);
  private readonly destroyRef = inject(DestroyRef);

  /** Avisa a tela que o deal foi apagado, para tirar o card do board sem recarregar. */
  readonly dealDeleted = output<string>();

  /** Ações do deal do último clique. */
  readonly items = signal<MenuItem[]>([]);

  @ViewChild('menu') private menu?: Menu;
  @ViewChild('contextMenu') private contextMenu?: ContextMenu;

  /**
   * Se o card do deal tem alguma ação para quem está vendo. Sem ação, nem as reticências aparecem
   * e o clique direito fica com o menu do navegador.
   */
  static hasActions(deal: PartnerDealResponse): boolean {
    return deal.canDelete || !!deal.deleteBlockedReason;
  }

  /** Abre o menu colado às reticências do card. */
  openFromButton(event: MouseEvent, deal: PartnerDealResponse): void {
    event.preventDefault();
    event.stopPropagation();
    this.contextMenu?.hide();
    this.items.set(this.buildItems(deal));
    this.menu?.toggle(event);
  }

  /** Abre o mesmo menu no ponto do clique com o botão direito. */
  openFromContextMenu(event: MouseEvent, deal: PartnerDealResponse): void {
    if (!DealActionsMenuComponent.hasActions(deal)) return;

    event.preventDefault();
    event.stopPropagation();
    this.menu?.hide();
    this.items.set(this.buildItems(deal));
    this.contextMenu?.show(event);
  }

  /**
   * As ações do deal. Desabilitada, a ação leva o motivo numa linha própria logo abaixo: item
   * desabilitado não mostra tooltip, e a regra precisa estar à vista de quem tentou.
   */
  private buildItems(deal: PartnerDealResponse): MenuItem[] {
    const items: MenuItem[] = [
      {
        label: 'Apagar deal',
        icon: 'pi pi-trash',
        disabled: !deal.canDelete,
        styleClass: 'deal-actions-menu__delete',
        command: () => this.confirmDelete(deal),
      },
    ];

    if (!deal.canDelete && deal.deleteBlockedReason) {
      // Habilitado e sem `command` de propósito: com todos os itens desabilitados, o Esc do
      // p-contextMenu (PrimeNG 21.1.3, `onEscapeKey`) procura o primeiro item válido, não acha e
      // estoura no console. Clicar no motivo só fecha o menu.
      items.push({
        label: deal.deleteBlockedReason,
        styleClass: 'deal-actions-menu__reason',
      });
    }

    return items;
  }

  /** Pede confirmação dizendo qual deal e o que acontece, e só então apaga. */
  private confirmDelete(deal: PartnerDealResponse): void {
    const title = `Apagar o deal #${deal.sequentialId} de ${deal.corporateName}?`;
    const description =
      'O deal sai do board e não aparece mais em Meus deals. Os documentos enviados continuam no ' +
      'Data Room da empresa, e fica registrado quem apagou e quando.';

    this.confirmationService
      .confirm(title, description, 'danger', 'Cancelar', 'Apagar deal')
      .pipe(
        filter(confirmed => confirmed),
        switchMap(() => this.partnersService.deleteDeal(deal.id)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.toast.showToastSuccess(`Deal #${deal.sequentialId} apagado.`);
          this.dealDeleted.emit(deal.id);
        },
        error: error => this.formErrorHandler.showToast(error, 'Não foi possível apagar o deal.'),
      });
  }
}
